import React, { useState, useEffect, Fragment, useRef, useMemo, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import CaseHomeNotesTasks from './CaseHomeNotesTasks';
import TaxRefundAffidavitPanel, {
  taxRefundAffidavitIsApplicable,
} from './components/TaxRefundAffidavitPanel';
import TrusteeRequirementDocumentLinks, {
  TrusteeDocumentMergePanel,
  trusteeRequirementUploads,
} from './components/TrusteeRequirementDocumentLinks';
import ServiceDocumentsPanel from './components/ServiceDocumentsPanel';
import DSOFormPanel from './components/DSOFormPanel';
import FinancialManagementFilingPanel from './components/FinancialManagementFilingPanel';
import InstallmentPaymentFilingPanel from './components/InstallmentPaymentFilingPanel';
import CaseTitleSearchPanel from './components/CaseTitleSearchPanel';
import CreditorMailingMatrixPanel from './components/CreditorMailingMatrixPanel';
import BNCCompliancePanel from './components/BNCCompliancePanel';
import {
  debtorRoleLabel,
  filingDebtors,
  primaryDebtor,
  sortDebtorsPrimaryFirst,
} from '../utils/debtors';
export default function CaseHome() {
  // Helper to display ISO date strings without timezone offset issues
  const formatLocalDate = isoString => {
    if (!isoString) return null;
    // Expecting YYYY-MM-DD or ISO; split by '-' for date part
    const [year, month, day] = isoString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  };
  // Helper to display ISO datetime strings with date and time, adjusting for offset so the time part appears as intended
  const formatLocalDateTime = isoString => {
    if (!isoString) return null;
    // Match date, time, and offset parts
    const match = isoString.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})([+\-]\d{2}):(\d{2})$/);
    if (!match) {
      // Fallback to built-in parser
      return new Date(isoString).toLocaleString();
    }
    const [, datePart, hh, mm, ss, offH, offM] = match;
    const [year, month, day] = datePart.split('-').map(Number);
    const hour = Number(hh);
    const minute = Number(mm);
    const second = Number(ss);
    // Offset parts, e.g. "-04" and "00"
    const offsetHours = Number(offH);
    const offsetMinutes = Number(offM);
    // Apply inverse offset to get displayed time
    const displayHour = hour;
    const displayMinute = minute + (-offsetMinutes);
    const dt = new Date(year, month - 1, day, displayHour, displayMinute, second);
    return dt.toLocaleString();
  };


  // Calculate monthly income from rate, schedule, and units
  const calcMonthlyIncome = (rate, schedule, units) => {
    const r = Number(rate);
    const u = units ? Number(units) : null;
    switch (schedule) {
      case 'hourly':
        // assume units = hours per week
        const hrs = u !== null ? u : 40;
        return (r * hrs * 52) / 12;
      case 'weekly':
        return (r * 52) / 12;
      case 'biweekly':
        return (r * 26) / 12;
      case 'semimonthly':
        return r * 2;
      case 'monthly':
        return r;
      case 'yearly':
        return r / 12;
      default:
        return 0;
    }
  };

  const { caseId } = useParams();
  const token = localStorage.getItem('accessToken');
  const API = process.env.REACT_APP_API_URL;
  const { caseInfo, loading: caseLoading, updateCaseInfo } = useOutletContext();
  // Calculate Filing Fee Deadline for Chapter 7 (date_filed + 120 days)
  let filingFeeDeadline = null;
  if (caseInfo?.date_filed) {
    const filedDate = new Date(caseInfo.date_filed);
    filedDate.setDate(filedDate.getDate() + 120);
    filingFeeDeadline = formatLocalDate(filedDate.toISOString());
  }
  const formattedSigningDate = useMemo(() => {
    if (!caseInfo?.case_signing_date) return '';
    const dt = new Date(caseInfo.case_signing_date);
    const datePart = dt.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const timePart = dt.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${datePart} at ${timePart}`;
  }, [caseInfo?.case_signing_date]);
  const [caseData, setCaseData] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const applyCaseData = useCallback((updatedCase) => {
    setCaseData(updatedCase);
    updateCaseInfo?.(updatedCase);
  }, [updateCaseInfo]);
  const refreshChecklist = useCallback(async () => {
    try {
      const response = await fetch(`${API}/case/${caseId}/checklist/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Couldn't refresh checklist");
      setChecklist(await response.json());
    } catch (refreshError) {
      console.error(refreshError);
    }
  }, [API, caseId, token]);
  const financialManagementItem = checklist.find(
    item => String(item.name || '').toLowerCase() === 'certificate of financial management'
  );
  const hasFinancialManagementUpload = Boolean(
    financialManagementItem?.uploads?.some(upload => upload.status !== 'rejected')
  );

  const [showEditCase, setShowEditCase] = useState(false);
  const [editingCaseData, setEditingCaseData] = useState(null);
  const saveCaseRef = useRef(null);
  const [showEditDebtor, setShowEditDebtor] = useState(false);
  const [editingDebtor, setEditingDebtor] = useState(null);
  const saveDebtorRef = useRef(null);

  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const savePropertyRef = useRef(null);

  // Edit Income Modal state
  const [showEditIncome, setShowEditIncome] = useState(false);
  const [editingIncomeData, setEditingIncomeData] = useState(null);
  const saveIncomeRef = useRef(null);

  // ===== Derived Trustee Requirements & Mailing Lists =====
  const hasTaxReturnChecklist = checklist.some(i => /tax\s*return/i.test(i.name || ''));
  const hasW2Checklist = checklist.some(i => /(^|\b)W2s\b/i.test(i.name || ''));
  const hasApplicableTaxReturnDocument = taxRefundAffidavitIsApplicable(checklist);
  const hasDsoExpense = expenses.some(
    expense => expense.category === 'alimony_child_support' && Number(expense.amount || 0) > 0
  );
  const hasDsoPayrollDeduction = (caseData?.debtors || []).some(debtor =>
    (debtor.employments || []).some(employment =>
      (employment.deductions || []).some(
        deduction => deduction.deduction_type === 'dso' && Number(deduction.amount || 0) > 0
      )
    )
  );
  const intakeData = caseData?.intake_data || {};
  const hasIntakeDsoExpense = [
    intakeData.childSupportAmount,
    intakeData.spouseChildSupportAmount,
  ].some(amount => Number(amount || 0) > 0);
  const hasIntakeDsoPayrollDeduction = [
    intakeData.childSupportDeducted,
    intakeData.spouseChildSupportDeducted,
  ].some(value => String(value || '').toLowerCase() === 'yes');
  const needsDsoForm =
    hasDsoExpense ||
    hasDsoPayrollDeduction ||
    hasIntakeDsoExpense ||
    hasIntakeDsoPayrollDeduction;

  const trusteeDocs = useMemo(() => {
    const docs = [];
    const ch = String(caseData?.chapter || '').trim();

    if (ch === '7') {
      (caseData?.vehicles || []).forEach(v => {
        const label = [v.year, v.make, v.model].filter(Boolean).join(' ');
        docs.push(`Title, official title report, or MVDS — ${label || 'Vehicle'}`);
      });
      if (hasTaxReturnChecklist) {
        docs.push('Most Recent tax return');
      }
      (caseData?.real_properties || []).forEach(p => {
        const addr = [p.street1, p.street2, p.city, p.state, p.zip_code]
          .filter(Boolean)
          .join(', ');
        docs.push(`Deed of Trust — ${addr || 'Real Property'}`);
      });
      if (needsDsoForm) {
        docs.push('DSO Form');
      }
      docs.push('Photo ID');
      docs.push('Social Security Card');
    } else if (ch === '13') {
      if (hasApplicableTaxReturnDocument) {
        docs.push('Send Tax Refund Affidavit');
      }
      if (hasTaxReturnChecklist) {
        docs.push('Current year and previous year tax returns');
      }
      if (hasW2Checklist) {
        docs.push('W2/1099s for current and previous year');
      }
      (caseData?.real_properties || []).forEach(p => {
        const addr = [p.street1, p.street2, p.city, p.state, p.zip_code]
          .filter(Boolean)
          .join(', ');
        docs.push(`Deed of Trust — ${addr || 'Real Property'}`);
      });
      if (needsDsoForm) {
        docs.push('DSO Form');
      }
      docs.push('Photo ID');
      docs.push('Social Security Card');
    }

    return docs;
  }, [caseData?.chapter, caseData?.vehicles, caseData?.real_properties, hasTaxReturnChecklist, hasW2Checklist, hasApplicableTaxReturnDocument, needsDsoForm]);
  const trusteeDocumentUploads = useMemo(() => {
    const seen = new Set();
    return trusteeDocs.flatMap((requirementName) => (
      trusteeRequirementUploads(checklist, requirementName)
    )).filter((upload) => {
      const key = String(upload.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [checklist, trusteeDocs]);

  const refreshCaseHome = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const [
        caseRes,
        checklistRes,
        creditorsRes,
        expensesRes,
      ] = await Promise.all([
        fetch(`${API}/case/${caseId}/overview/`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }),
        fetch(`${API}/case/${caseId}/checklist/`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }),
        fetch(`${API}/case/${caseId}/creditors/`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }),
        fetch(`${API}/cases/${caseId}/expenses/`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }),
      ]);

      if (!caseRes.ok) throw new Error("Couldn't load case");
      if (!checklistRes.ok) throw new Error("Couldn't load checklist");
      if (!creditorsRes.ok) throw new Error("Couldn't load creditors");
      if (!expensesRes.ok) throw new Error("Couldn't load expenses");

      const [nextCase, nextChecklist, nextCreditors, nextExpenses] = await Promise.all([
        caseRes.json(),
        checklistRes.json(),
        creditorsRes.json(),
        expensesRes.json(),
      ]);
      applyCaseData(nextCase);
      setChecklist(nextChecklist);
      setCreditors(nextCreditors);
      setExpenses(nextExpenses);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [API, applyCaseData, caseId, token]);

  useEffect(() => {
    refreshCaseHome({ showLoading: true });
  }, [refreshCaseHome]);

  if (loading || !caseData) {
    return (
      <div className="flex h-screen items-center justify-center">
        <span className="text-gray-500">Loading…</span>
      </div>
    );
  }
  if (caseLoading) {
    return <p>Loading case data…</p>;
  }

  if (!caseInfo) {
    return <p>Failed to load case data.</p>;
  }

  const completed = checklist.filter(i => i.status === 'accepted').length;
  const total = checklist.length;
  const pct = total > 0 ? (completed / total) * 100 : 0;

  // group all uploads
  const uploadsAll = checklist.flatMap(item => item.uploads || []);
  const acceptedCount = uploadsAll.filter(u => u.status === 'accepted').length;
  const awaitingReviewCount = uploadsAll.filter(u => u.status === 'awaiting_review').length;
  const rejectedCount = uploadsAll.filter(u => u.status === 'rejected').length;
  // items with no uploads
  const unsentCount = checklist.filter(item => (item.uploads || []).length === 0).length;

  const summary = creditors.reduce((acc, c) => {
    acc.total.count += 1;
    acc.total.amount += Number(c.balance || 0);
    const t = c.debt_type;
    if (!acc[t]) acc[t] = { count: 0, amount: 0 };
    acc[t].count += 1;
    acc[t].amount += Number(c.balance || 0);
    return acc;
  }, { total: { count: 0, amount: 0 } });

  const orderedDebtors = sortDebtorsPrimaryFirst(caseData.debtors);
  const debtor = primaryDebtor(orderedDebtors, { filingOnly: true }) || {};


  return (
    <div className="max-w-7xl mx-auto p-6 space-y-12">
      {/* Hero Header */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Case Overview: <span className="text-indigo-600">{caseData.case_id}</span>
          </h1>
          <p className="text-gray-700">
            <span className="font-semibold">Chapter:</span> {caseData.chapter}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold">Status:</span> {caseData.status}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold">Fees Paid:</span> ${caseData.fees_paid_prior}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold">Total Attorney Fees:</span> ${caseData.pre_filing_fees}
          </p>
            <p className="text-gray-700">
            <span className="font-semibold">Signing date:</span>{' '}
            {caseData.case_signing_date
              ? formattedSigningDate
              : '—'}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold">Date Filed:</span>{' '}
            {caseData.date_filed
              ? formatLocalDate(caseData.date_filed)
              : '—'}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold">Case Number:</span>{' '}
            {caseData.case_number || '—'}
          </p>
          {caseData.case_number && (
            <>
              {caseData.meeting_date != null && (
                <p className="text-gray-700">
                  <span className="font-semibold">341 Meeting:</span>{' '}
                  {formatLocalDateTime(caseData.meeting_date)}
                  <span className="font-semibold">Admin hearing:</span> {caseData.admin_hearing ? 'Yes' : 'No'}
                </p>
              )}
              {caseData.meeting_id != null && (
                <p className="text-gray-700">
                  <span className="font-semibold">Zoom Meeting ID:</span>{' '}
                  {caseData.meeting_id}
                </p>
              )}
              {caseData.meeting_passcode != null && (
                <p className="text-gray-700">
                  <span className="font-semibold">Zoom Meeting Passcode:</span>{' '}
                  {caseData.meeting_passcode}
                </p>
              )}
              {caseData.trustee != null && (
                <p className="text-gray-700">
                  <span className="font-semibold">Trustee:</span>{' '}
                  {caseData.trustee}
                </p>
              )}
              {caseData.reaffirmation_deadline && caseData.chapter === '7' && (
                <p className="text-gray-700">
                  <span className="font-semibold">Reaffirmation Deadline:</span>{' '}
                  {formatLocalDate(caseData.reaffirmation_deadline)}
                </p>
              )}
              {/* Filing Fee Deadline, Amount, Paid for Chapter 7 */}
              {caseData.chapter === '7' && filingFeeDeadline && (
                <p className="text-gray-700">
                  <span className="font-semibold">Filing Fee Deadline:</span>{' '}
                  {filingFeeDeadline}
                </p>
              )}
              {caseData.chapter === '7' && (
                <>
                  <p className="text-gray-700">
                    <span className="font-semibold">Filing Fee Amount:</span>{' '}
                    ${caseData.filing_fee_amount ?? '—'}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Filing Fees Paid:</span>{' '}
                    ${caseData.filing_fees_paid ?? '—'}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Filing Fees Receipted:</span>{' '}
                    ${caseData.filing_fees_receipted ?? '—'}
                  </p>
                  {caseData.case_number
                    && Number(caseData.filing_fees_paid || 0) > Number(caseData.filing_fees_receipted || 0) && (
                    <InstallmentPaymentFilingPanel caseId={caseId} />
                  )}
                </>
              )}
              {caseData.financial_management_deadline && (
                <p className="text-gray-700">
                  <span className="font-semibold">Financial Management Deadline:</span>{' '}
                  {formatLocalDate(caseData.financial_management_deadline)}
                </p>
              )}
              {caseData.financial_management_deadline && (
                <p className="text-gray-700">
                  {caseData.financial_management_course_filed ? 
                  <span className="font-semibold">Financial Management Certificate Filed: Yes</span>
                  : <span className="font-semibold">Financial Management Certificate Filed: No</span>
                  }
                </p>
              )}
              {caseData.financial_management_deadline && (
                <p className="text-gray-700">
                  {caseData.trustee_docs ? 
                  <span className="font-semibold">Trustee Docs done: Yes</span>
                  : <span className="font-semibold">Trustee Docs done: No</span>
                  }
                </p>
              )}
              {caseData.chapter === '13' && caseData.proof_of_claims_deadline && (
                <p className="text-gray-700">
                  <span className="font-semibold">Proof of Claims Deadline:</span>{' '}
                  {formatLocalDate(caseData.proof_of_claims_deadline)}
                </p>
              )}
              {caseData.chapter === '13' && caseData.confirmation_date && (
                <p className="text-gray-700">
                  <span className="font-semibold">Confirmation Date:</span>{' '}
                  {formatLocalDate(caseData.confirmation_date)}
                </p>
              )}
            </>
          )}
          <button
            onClick={() => {
              setEditingCaseData({ ...caseData });
              setShowEditCase(true);
            }}
            className="mt-4 bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700"
          >
            Edit Case
          </button>
        </div>
        {/* Debtors */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-medium text-gray-800 mb-4">Debtors</h2>
          {caseData.debtors.length < 2 && (
            <button
              onClick={() => {
                setEditingDebtor({
                  is_primary: false,
                  is_filing: false,
                  first_name: '',
                  second_name: '',
                  last_name: '',
                  suffix: '',
                  dob: '',
                  marital_status: '',
                  street: '',
                  street2: '',
                  city: '',
                  state: '',
                  zip_code: '',
                  county: '',
                  mailing_street: '',
                  mailing_street2: '',
                  mailing_city: '',
                  mailing_state: '',
                  mailing_zip_code: '',
                  email: '',
                  phone: '',
                  debn_notice: false,
                });
                setShowEditDebtor(true);
              }}
              className="mb-4 inline-block px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              + Add Spouse
            </button>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {orderedDebtors.map((d) => (
            <div
              key={d.id}
              className="cursor-pointer border border-gray-100 hover:bg-gray-50 p-3 rounded"
              onClick={() => {
                setEditingDebtor({ ...d });
                setShowEditDebtor(true);
              }}
            >
              <p className="mb-1 font-semibold text-indigo-700">
                {debtorRoleLabel(d, orderedDebtors)}
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Name:</span>{' '}
                {[d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ')}
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Address:</span>{' '}
                {[d.street, d.street2].filter(Boolean).join(' ')}, {d.city}, {d.state}{' '}
                {d.zip_code}
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">SSN:</span>{' '}
                {d.ssn || '—'}
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Email:</span>{' '}
                {d.email || '—'}
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Phone:</span>{' '}
                {d.phone || '—'}
              </p>
            </div>
          ))}
          </div>
        </div>
      </div>

      {hasFinancialManagementUpload && !caseData.financial_management_course_filed && (
        <section className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Financial Management Certificate</h2>
              <p className="mt-1 text-sm text-gray-600">
                A certificate is uploaded for this case and has not yet been filed with the court.
              </p>
            </div>
            <FinancialManagementFilingPanel
              caseId={caseId}
              caseNumber={caseData.case_number}
              onFiled={() => applyCaseData({
                ...caseData,
                financial_management_course_filed: true,
              })}
            />
          </div>
        </section>
      )}

      {/* Quick Stats and Creditors side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Checklist */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Document Checklist Status</h3>
          {/* Document Checklist grouped */}
          {(() => {
            const acceptedItems = checklist.filter(item =>
              (item.uploads || []).some(u => u.status === 'accepted')
            );
            const awaitingItems = checklist.filter(item =>
              !acceptedItems.includes(item) &&
              (item.uploads || []).some(u => u.status === 'awaiting_review')
            );
            const notSubmittedItems = checklist.filter(item =>
              !acceptedItems.includes(item) &&
              !awaitingItems.includes(item)
            );
            const groups = [
              { key: 'accepted', label: 'Accepted', items: acceptedItems },
              { key: 'awaiting', label: 'Awaiting Review', items: awaitingItems },
              { key: 'not_submitted', label: 'Not Submitted', items: notSubmittedItems },
            ];
            return groups.map(group => (
              <details key={group.key} className="mb-2">
                <summary className="font-semibold">
                  {group.label} ({group.items.length})
                </summary>
                <ul className="mt-2 ml-4 list-disc list-inside">
                  {group.items.map(i => (
                    <li key={i.id}>{i.name}</li>
                  ))}
                </ul>
              </details>
            ));
          })()}
        </div>
        {/* Creditors */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Creditors</h3>
          <p className="text-gray-700"><span className="font-semibold">Total Accounts:</span> {summary.total.count}</p>
          <p className="text-gray-700 mb-4"><span className="font-semibold">Total Balance:</span> ${summary.total.amount.toLocaleString()}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {['secured','priority','unsecured'].map(type => (
              <div key={type} className="text-center">
                <p className="font-semibold capitalize">{type}</p>
                <p>{summary[type]?.count||0} accounts</p>
                <p>${(summary[type]?.amount||0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vehicles and Real Property side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Vehicles */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Vehicles</h3>
          <button
            onClick={() => {
              setEditingVehicle({
                year: '',
                make: '',
                model: '',
                mileage: '',
                monthly_payment: '',
                value_total: '',
                ownership: '',
              });
              setShowVehicleModal(true);
            }}
            className="mb-4 inline-block px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            + Add Vehicle
          </button>
          {caseData.vehicles.map(v => {
            const linked = creditors.find(c =>
              c.asset_links?.some(link => link.asset_type==='vehicle' && String(link.object_id)===String(v.id))
            );
            return (
              <div
                key={v.id}
                className="mb-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                onClick={() => {
                  setEditingVehicle({ ...v });
                  setShowVehicleModal(true);
                }}
              >
                <p className="text-gray-700 font-semibold">{v.year} {v.make} {v.model}</p>
                <p className="text-gray-700">
                  <span className="font-medium">Value:</span>{' '}
                  {v.value_total || '—'}
                </p>
                {linked && (
                  <>
                    <p className="text-gray-700">
                      <span className="font-medium">Balance:</span> ${linked.balance} - {linked.account_name}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Monthly Payment:</span>{' '}
                      {linked.monthly_payment || '—'}
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>
    {/* Real Property */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-800 mb-4">Real Property</h3>
      <button
        onClick={() => {
          setEditingProperty({
            street1: '', street2: '', city: '', state: '', zip_code: '', county: '',
            monthly_payment: '', second_monthly_payment: '', principal_residence: false,
            property_type: '', ownership: '', ownership_type: '', community_property: false,
            other_info: '', value_total: '', value_ownership_portion: '',
            exemption_code: '', exemption_amount: '',
          });
          setShowPropertyModal(true);
        }}
        className="mb-4 inline-block px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        + Add Property
      </button>
      {caseData.real_properties.map(p => {
        const normalizeAddressPart = value => String(value || '').trim().toLowerCase();
        const intakeProperties = [
          {
            street: intakeData.street,
            zip: intakeData.zip,
            mortgageCreditor: intakeData.mortgageCreditor,
            mortgageBalance: intakeData.mortgageBalance,
            mortgageMonthlyPayment: intakeData.mortgageMonthlyPayment,
            mortgagePaymentStatus: intakeData.mortgagePaymentStatus,
            mortgageMonthsBehind: intakeData.mortgageMonthsBehind,
            secondMortgageCreditor: intakeData.secondMortgageCreditor,
            secondMortgageBalance: intakeData.secondMortgageBalance,
            secondMortgageMonthlyPayment: intakeData.secondMortgageMonthlyPayment,
            secondMortgagePaymentStatus: intakeData.secondMortgagePaymentStatus,
            secondMortgageMonthsBehind: intakeData.secondMortgageMonthsBehind,
          },
          ...(intakeData.otherRealEstateProperties || []),
          ...(intakeData.spouseOtherRealEstateProperties || []),
        ];
        const intakeProperty = p.principal_residence
          ? intakeProperties[0]
          : intakeProperties.slice(1).find(property =>
              normalizeAddressPart(property.street) === normalizeAddressPart(p.street1) &&
              normalizeAddressPart(property.zip) === normalizeAddressPart(p.zip_code)
            );
        const linkedCreditors = creditors.filter(c =>
          c.asset_links?.some(link =>
            link.asset_type === 'realproperty' && String(link.object_id) === String(p.id)
          )
        );

        return (
          <div
            key={p.id}
            className="mb-4 cursor-pointer hover:bg-gray-50 p-2 rounded"
            onClick={() => {
              setEditingProperty({ ...p });
              setShowPropertyModal(true);
            }}
          >
            <p className="text-gray-700 font-semibold">{p.street1}{p.street2 ? `, ${p.street2}` : ''}, {p.city}, {p.state} {p.zip_code}</p>
            <p className="text-gray-700"><span className="font-medium">Value:</span> {p.value_total || '—'}</p>
            <p className="text-gray-700"><span className="font-medium">Property Type:</span> {p.property_type?.replace(/_/g, ' ') || '—'}</p>
            <p className="text-gray-700"><span className="font-medium">Ownership:</span> {p.ownership?.replace(/_/g, ' ') || '—'}</p>
            <p className="text-gray-700"><span className="font-medium">First Mortgage Payment:</span> {p.monthly_payment || intakeProperty?.mortgageMonthlyPayment || '—'}</p>
            {intakeProperty?.mortgageCreditor && <p className="text-gray-700"><span className="font-medium">First Mortgage Creditor:</span> {intakeProperty.mortgageCreditor}</p>}
            {intakeProperty?.mortgageBalance && <p className="text-gray-700"><span className="font-medium">First Mortgage Balance:</span> {intakeProperty.mortgageBalance}</p>}
            {intakeProperty?.mortgagePaymentStatus && <p className="text-gray-700"><span className="font-medium">First Mortgage Current:</span> {intakeProperty.mortgagePaymentStatus}</p>}
            {intakeProperty?.mortgageMonthsBehind && <p className="text-gray-700"><span className="font-medium">First Mortgage Months Behind:</span> {intakeProperty.mortgageMonthsBehind}</p>}
            <p className="text-gray-700"><span className="font-medium">Second Mortgage Payment:</span> {p.second_monthly_payment || intakeProperty?.secondMortgageMonthlyPayment || '—'}</p>
            {intakeProperty?.secondMortgageCreditor && <p className="text-gray-700"><span className="font-medium">Second Mortgage Creditor:</span> {intakeProperty.secondMortgageCreditor}</p>}
            {intakeProperty?.secondMortgageBalance && <p className="text-gray-700"><span className="font-medium">Second Mortgage Balance:</span> {intakeProperty.secondMortgageBalance}</p>}
            {intakeProperty?.secondMortgagePaymentStatus && <p className="text-gray-700"><span className="font-medium">Second Mortgage Current:</span> {intakeProperty.secondMortgagePaymentStatus}</p>}
            {intakeProperty?.secondMortgageMonthsBehind && <p className="text-gray-700"><span className="font-medium">Second Mortgage Months Behind:</span> {intakeProperty.secondMortgageMonthsBehind}</p>}
            {linkedCreditors.length > 0 && (
              <div className="mt-2">
                <p className="font-medium text-gray-700">Linked Secured Claims:</p>
                <ul className="ml-4 list-disc list-inside text-gray-700">
                  {linkedCreditors.map(creditor => (
                    <li key={creditor.id}>{creditor.account_name}: ${creditor.balance}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
      </div>

      {/* Vehicle Edit Modal */}
      {showVehicleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingVehicle.id ? 'Edit Vehicle' : 'Add Vehicle'}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Year & Make */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Year</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-2 py-1"
                  value={editingVehicle.year || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, year: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Make</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-2 py-1"
                  value={editingVehicle.make || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, make: e.target.value })}
                />
              </div>
              {/* Model & Mileage */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Model</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-2 py-1"
                  value={editingVehicle.model || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, model: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Mileage</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-2 py-1"
                  value={editingVehicle.mileage || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, mileage: e.target.value })}
                />
              </div>
              {/* Value & Monthly Payment */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Value Total</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-2 py-1"
                  value={editingVehicle.value_total || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, value_total: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Monthly Payment</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-2 py-1"
                  value={editingVehicle.monthly_payment || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, monthly_payment: e.target.value })}
                />
              </div>
              {/* Ownership Dropdown full width */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Ownership</label>
                <select
                  className="mt-1 block w-full border rounded px-2 py-1"
                  value={editingVehicle.ownership || ''}
                  onChange={e => setEditingVehicle({ ...editingVehicle, ownership: e.target.value })}
                >
                  <option value="" disabled>Select ownership</option>
                  <option value="debtor1">Debtor 1</option>
                  <option value="debtor2">Debtor 2</option>
                  <option value="d1_d2">Debtor 1 & Debtor 2</option>
                  <option value="joint_third">Joint with Third Party</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-4">
              {editingVehicle.id && (
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/case/${caseId}/vehicles/${editingVehicle.id}/`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!response.ok) throw new Error('Delete failed');
                      applyCaseData({
                        ...caseData,
                        vehicles: caseData.vehicles.filter(v => v.id !== editingVehicle.id)
                      });
                      setShowVehicleModal(false);
                    } catch (err) {
                      console.error(err);
                      alert('Failed to delete vehicle');
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded"
                >
                  Remove
                </button>
              )}
              <button
                onClick={async () => {
                  try {
                    let saved;
                    if (editingVehicle.id) {
                      // Update existing
                      const resp = await fetch(`${API}/case/${caseId}/vehicles/${editingVehicle.id}/`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(editingVehicle),
                      });
                      if (!resp.ok) throw new Error('Update failed');
                      saved = await resp.json();
                    } else {
                      // Create new
                      const resp = await fetch(`${API}/case/${caseId}/vehicles/`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(editingVehicle),
                      });
                      if (!resp.ok) throw new Error('Create failed');
                      saved = await resp.json();
                    }
                    const updatedList = editingVehicle.id
                      ? caseData.vehicles.map(v => v.id === saved.id ? saved : v)
                      : [...caseData.vehicles, saved];
                    applyCaseData({ ...caseData, vehicles: updatedList });
                    setShowVehicleModal(false);
                  } catch (err) {
                    console.error(err);
                    alert('Failed to save vehicle');
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                Save
              </button>
              <button
                onClick={() => setShowVehicleModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <CaseHomeNotesTasks caseId = {caseId}></CaseHomeNotesTasks>
      <CaseTitleSearchPanel caseId={caseId} onDocumentCreated={refreshChecklist} />
      {caseData.case_number && (
        <CreditorMailingMatrixPanel caseId={caseId} />
      )}
      {/* Trustee quick view */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-800 mb-2">Trustee Documents</h3>
            <div className="text-gray-800 mb-3">
              <span className="font-semibold">Trustee:</span> {caseData.trustee || '—'}
            </div>
            {caseData.trustee && (
              <div className="mb-4">
                <span className="font-semibold">Upload Site:</span>{' '}
                {(() => {
                  switch (caseData.trustee) {
                    case 'Debra Miller':
                      return <a href="https://www.trustee13.com/TrusteeList.aspx" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">trustee13.com</a>;
                    case 'Douglas Payne':
                    case 'Stephen Duncan':
                      return <a href="https://documentportal.stretto.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">documentportal.stretto.com</a>;
                    case 'Margaret Fugate':
                      return <a href="https://documentdelivery.axosfs.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">documentdelivery.axosfs.com</a>;
                    default:
                      return <span className="text-gray-600">(No site listed)</span>;
                  }
                })()}
              </div>
            )}
            <div className="mb-6">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${caseData.trustee_docs ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                Trustee Documents: {caseData.trustee_docs ? 'Complete' : 'Not yet done'}
              </span>
            </div>

            {/* Trustee Documents List */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-2">Trustee Documents ({caseData.chapter === '7' ? 'Chapter 7' : 'Chapter 13'})</h4>
              {trusteeDocs.length > 0 ? (
                <ul className="list-disc list-inside text-gray-800 space-y-1">
                  {trusteeDocs.map((d, i) => (
                    <li key={i} className={['Send Tax Refund Affidavit', 'DSO Form'].includes(d) ? 'list-none' : ''}>
                      {d === 'Send Tax Refund Affidavit' ? (
                        <TaxRefundAffidavitPanel
                          caseId={caseId}
                          caseNumber={caseData.case_number}
                          chapter={caseData.chapter}
                          debtorEmails={filingDebtors(caseData.debtors).map((debtorItem) => debtorItem.email).filter(Boolean)}
                          initialChecklist={checklist}
                          applicableOverride
                        />
                      ) : d === 'DSO Form' ? (
                        <DSOFormPanel
                          caseId={caseId}
                          caseNumber={caseData.case_number}
                          chapter={caseData.chapter}
                          initialChecklist={checklist}
                          initialCaseData={caseData}
                          initialExpenses={expenses}
                        />
                      ) : (
                        <>
                          <span>{d}</span>
                          <TrusteeRequirementDocumentLinks
                            uploads={trusteeRequirementUploads(checklist, d)}
                          />
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-600">No specific trustee documents derived yet.</p>
              )}
              <TrusteeDocumentMergePanel
                caseId={caseId}
                caseNumber={caseData.case_number}
                uploads={trusteeDocumentUploads}
              />
            </div>

          </div>

          <div className="shrink-0">
            <button
              onClick={() => {
                setEditingCaseData({ ...caseData });
                setShowEditCase(true);
              }}
              className="text-indigo-600 hover:text-indigo-800 text-sm underline"
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      {(caseData.chapter === '7' || caseData.chapter === '13') && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Filing Service Documents</h3>
          <ServiceDocumentsPanel
            caseId={caseId}
            caseNumber={caseData.case_number}
            chapter={caseData.chapter}
            serviceDocs={caseData.service_docs}
            allowMarkComplete
            onCompleted={applyCaseData}
          />
        </div>
      )}

      {caseData.case_number && (caseData.chapter === '7' || caseData.chapter === '13') && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">
            General Order 2026-03 Compliance
          </h3>
          <BNCCompliancePanel caseId={caseId} />
        </div>
      )}

      {/* Income full width */}
      <div>
        {/* Income */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Income</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {orderedDebtors.map(d => {
              const fullName = [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
              return (
                <div key={d.id}>
                  <p className="font-semibold">{fullName}{!d.is_filing && ' (Non-filing spouse)'}</p>
                  {/* Employment Income */}
                  <div className="mb-2">
                    <h4 className="font-medium">Employment Income</h4>
                    <div className="mt-2 space-y-3 text-gray-700">
                      {(d.employments || []).map((emp, employmentIndex, allEmployments) => {
                        const monthlyIncome = calcMonthlyIncome(emp.pay_rate, emp.pay_schedule, emp.pay_units);
                        const totalMonthlyDeductions = (emp.deductions || []).reduce(
                          (sum, det) => sum + Number(det.amount || 0),
                          0
                        );
                        const netMonthly = monthlyIncome - totalMonthlyDeductions;
                        const isPriorEmployment = Boolean(emp.end_date);
                        const matchingEmployments = allEmployments.filter(item => Boolean(item.end_date) === isPriorEmployment);
                        const position = allEmployments
                          .slice(0, employmentIndex + 1)
                          .filter(item => Boolean(item.end_date) === isPriorEmployment).length;
                        const employmentLabel = isPriorEmployment
                          ? `Prior Employment${matchingEmployments.length > 1 ? ` ${position}` : ''}`
                          : position === 1
                            ? 'Primary Employment'
                            : position === 2
                              ? 'Second Employment'
                              : `Additional Employment ${position}`;
                        const employerAddress = [
                          emp.employer_street,
                          emp.employer_street2,
                          emp.employer_city,
                          emp.employer_state,
                          emp.employer_zip,
                        ].filter(Boolean).join(', ');
                        return (
                          <div key={emp.id || employmentIndex} className="border rounded p-3 bg-gray-50">
                            <p className="font-semibold text-gray-800">{employmentLabel}: {emp.employer || '—'}</p>
                            {emp.occupation && <p><span className="font-medium">Occupation:</span> {emp.occupation}</p>}
                            {employerAddress && <p><span className="font-medium">Employer Address:</span> {employerAddress}</p>}
                            <p><span className="font-medium">Pay:</span> {emp.pay_rate} {emp.pay_schedule}</p>
                            {emp.pay_units && <p><span className="font-medium">Units:</span> {emp.pay_units}</p>}
                            {emp.length_of_employment && <p><span className="font-medium">Length of Employment:</span> {emp.length_of_employment}</p>}
                            {emp.start_date && <p><span className="font-medium">Start Date:</span> {formatLocalDate(emp.start_date)}</p>}
                            {emp.end_date && <p><span className="font-medium">End Date:</span> {formatLocalDate(emp.end_date)}</p>}
                            <p>
                              <span className="font-medium">Payment Advices/Pay Stubs:</span>{' '}
                              {emp.has_payment_advices === false ? 'No' : 'Yes'}
                            </p>
                            <p><span className="font-medium">Monthly Income:</span> ${monthlyIncome.toFixed(2)}</p>
                            {emp.deductions?.length > 0 && (
                              <div className="mt-1">
                                <p>Total Monthly Deductions: ${totalMonthlyDeductions.toFixed(2)}</p>
                                <ul className="ml-4 list-disc list-inside">
                                  {emp.deductions.map(det => (
                                    <li key={det.id}>
                                      {det.deduction_type.replace(/_/g,' ').replace(/\b\w/g,c => c.toUpperCase())}: ${det.amount} monthly
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <p className="font-semibold">Net Monthly Income: ${netMonthly.toFixed(2)}</p>
                          </div>
                        );
                      })}
                      {(d.employments || []).length === 0 && (
                        <p className="text-gray-500">No employment records.</p>
                      )}
                    </div>
                  </div>
                  {/* Fixed Income Sources */}
                  <div className="mb-2">
                    <h4 className="font-medium">Fixed Income</h4>
                    <ul className="ml-4 list-disc list-inside text-gray-700">
                      {d.interest_dividend_income && <li>Interest/Dividends: ${d.interest_dividend_income}</li>}
                      {d.family_support_payments && <li>Family Support: ${d.family_support_payments}</li>}
                      {d.unemployment_income && <li>Unemployment: ${d.unemployment_income}</li>}
                      {d.social_security_income && <li>Social Security: ${d.social_security_income}</li>}
                      {d.pension_income && <li>Pension: ${d.pension_income}</li>}
                    </ul>
                  </div>
                  {/* Government Assistance */}
                  {d.gov_assistances.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Government Assistance</h4>
                      <ul className="ml-4 list-disc list-inside text-gray-700">
                        {d.gov_assistances.map(ga => (
                          <li key={ga.id}>{ga.name}: ${ga.amount}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Other Income Items */}
                  {d.other_income_items.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Other Income</h4>
                      <ul className="ml-4 list-disc list-inside text-gray-700">
                        {d.other_income_items.map(oi => (
                          <li key={oi.id}>{oi.name}: ${oi.amount}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Expense Contributions */}
                  {d.expense_contributions.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Expense Contributions</h4>
                      <ul className="ml-4 list-disc list-inside text-gray-700">
                        {d.expense_contributions.map(ec => (
                          <li key={ec.id}>{ec.name}: ${ec.amount}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Business Income */}
                  {d.business_incomes.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Business Income</h4>
                      {d.business_incomes.map(bi => {
                        const totalExpenses = bi.expenses.reduce((sum, e) => sum + Number(e.amount), 0);
                        const netIncome = Number(bi.gross_revenue) - totalExpenses;
                        return (
                          <div key={bi.id} className="ml-4 mb-1">
                            <p>{bi.name}: Gross ${bi.gross_revenue}, Expenses ${totalExpenses}, Net ${netIncome}</p>
                            {bi.expenses.length > 0 && (
                              <ul className="ml-4 list-disc list-inside text-gray-700">
                                {bi.expenses.map(exp => (
                                  <li key={exp.id}>{exp.expense_name}: ${exp.amount}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            onClick={() => {
              setEditingIncomeData(orderedDebtors.map(d => ({ ...d })));
              setShowEditIncome(true);
            }}
            className="mt-4 bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700"
          >
            Edit Income
          </button>
        </div>
      </div>

      {/* Initial Intake Summary */}
      {caseData?.intake_data && (
        <details className="bg-white rounded-lg shadow p-0">
          <summary className="cursor-pointer select-none list-none px-6 py-4 flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-800">Initial Intake Summary</span>
            <span className="text-sm text-gray-500">(click to expand/collapse)</span>
          </summary>

          <div className="px-6 pb-6">
            {(() => {
              const i = caseData.intake_data || {};
              const hasAny = Object.keys(i || {}).length > 0;
              if (!hasAny) return <p className="text-gray-600">No intake data captured.</p>;

              const sectionTitle = (t) => (
                <h4 className="text-lg font-bold text-indigo-700 mb-3">{t}</h4>
              );

              const Section = ({ title, children }) => (
                <div className="border-t border-gray-300 pt-4 mt-6">
                  {sectionTitle(title)}
                  {children}
                </div>
              );

              const Card = ({ children }) => (
                <div className="bg-gray-50 p-3 rounded border">{children}</div>
              );

              const hasDisplayValue = value =>
                value !== undefined &&
                value !== null &&
                value !== '' &&
                (!Array.isArray(value) || value.length > 0);

              const firstDisplayValue = (...values) => values.find(hasDisplayValue);

              const Field = ({ label, value }) =>
                hasDisplayValue(value) ? (
                  <p className="text-gray-700">
                    <span className="font-medium">{label}:</span>{' '}
                    {Array.isArray(value)
                      ? value.join(', ')
                      : typeof value === 'boolean'
                        ? (value ? 'Yes' : 'No')
                        : String(value)}
                  </p>
                ) : null;

              const IntakePropertyCard = ({ property, title }) => {
                const hasFirstMortgage = [
                  property.hasMortgage,
                  property.mortgageCreditor,
                  property.mortgageBalance,
                  property.mortgageMonthlyPayment,
                  property.mortgagePaymentStatus,
                  property.mortgageMonthsBehind,
                ].some(hasDisplayValue);
                const hasSecondMortgage = [
                  property.hasSecondMortgage,
                  property.secondMortgageCreditor,
                  property.secondMortgageBalance,
                  property.secondMortgageMonthlyPayment,
                  property.secondMortgagePaymentStatus,
                  property.secondMortgageMonthsBehind,
                ].some(hasDisplayValue);

                return (
                  <Card>
                    <p className="font-semibold text-gray-800 mb-1">{title}</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Street" value={property.street} />
                      <Field label="Street 2" value={property.street2} />
                      <Field label="City" value={property.city} />
                      <Field label="State" value={property.state} />
                      <Field label="ZIP" value={property.zip} />
                      <Field label="Property Type" value={property.propertyType} />
                      <Field label="Ownership" value={property.ownership} />
                      <Field label="Value" value={firstDisplayValue(property.homeValue, property.value)} />
                    </div>
                    {hasFirstMortgage && (
                      <div className="mt-3">
                        <p className="font-medium text-gray-700">First Mortgage</p>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Field label="Has Mortgage" value={property.hasMortgage} />
                          <Field label="Creditor" value={property.mortgageCreditor} />
                          <Field label="Balance" value={property.mortgageBalance} />
                          <Field label="Monthly Payment" value={property.mortgageMonthlyPayment} />
                          <Field label="Current" value={property.mortgagePaymentStatus} />
                          <Field label="Months Behind" value={property.mortgageMonthsBehind} />
                        </div>
                      </div>
                    )}
                    {hasSecondMortgage && (
                      <div className="mt-3">
                        <p className="font-medium text-gray-700">Second Mortgage</p>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Field label="Has Second Mortgage" value={property.hasSecondMortgage} />
                          <Field label="Creditor" value={property.secondMortgageCreditor} />
                          <Field label="Balance" value={property.secondMortgageBalance} />
                          <Field label="Monthly Payment" value={property.secondMortgageMonthlyPayment} />
                          <Field label="Current" value={property.secondMortgagePaymentStatus} />
                          <Field label="Months Behind" value={property.secondMortgageMonthsBehind} />
                        </div>
                      </div>
                    )}
                  </Card>
                );
              };

              return (
                <div>
                  {/* Respondent */}
                  <Section title="Respondent">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Field label="First Name" value={i.firstName} />
                      <Field label="Middle Name" value={i.middleName} />
                      <Field label="Last Name" value={i.lastName} />
                      <Field label="DOB" value={i.dob} />
                      <Field label="Phone" value={i.phone} />
                      <Field label="Email" value={i.email} />
                      <Field label="Requested Chapter" value={i.chapter} />
                      <Field label="Chapter Override" value={i.override_chapter} />
                      <Field label="Pre-filing Fee Override" value={i.override_prefiling_fee} />
                      <Field label="Marital Status" value={i.maritalStatus} />
                      <Field label="Years Married" value={i.yearsMarried} />
                      <Field label="Status Duration" value={i.statusDuration} />
                    </div>
                  </Section>

                  {/* Spouse */}
                  {(i.spouseFirstName || i.spouseLastName || i.spouseDOB || i.spouseEmail || i.spousePhone || i.spouseFiling) && (
                    <Section title="Spouse">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Field label="First Name" value={i.spouseFirstName} />
                        <Field label="Middle Name" value={i.spouseMiddleName} />
                        <Field label="Last Name" value={i.spouseLastName} />
                        <Field label="DOB" value={i.spouseDOB} />
                        <Field label="Phone" value={i.spousePhone} />
                        <Field label="Email" value={i.spouseEmail} />
                        <Field label="Is Filing" value={i.spouseFiling} />
                      </div>
                    </Section>
                  )}

                  {/* Bankruptcy History */}
                  <Section title="Bankruptcy History (Self-reported)">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Filed Before" value={i.bankruptcyFiled} />
                      <Field label="Chapter" value={i.bankruptcyChapter} />
                      <Field label="Years Ago" value={i.bankruptcyYearsAgo} />
                      <Field label="Case Number" value={i.bankruptcyCaseNumber} />
                    </div>
                    <hr className="my-4 border-gray-300" />
                    <h5 className="text-md font-semibold text-gray-800 mb-2">Spouse</h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Filed Before" value={i.spouseBankruptcyFiled} />
                      <Field label="Chapter" value={i.spouseBankruptcyChapter} />
                      <Field label="Years Ago" value={i.spouseBankruptcyYearsAgo} />
                      <Field label="Case Number" value={i.spouseBankruptcyCaseNumber} />
                    </div>
                  </Section>

                  {/* Address */}
                  <Section title="Address">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Field label="Street" value={i.street} />
                      <Field label="City" value={i.city} />
                      <Field label="State" value={i.state} />
                      <Field label="ZIP" value={i.zip} />
                      <Field label="County" value={i.county} />
                    </div>
                  </Section>

                  <Section title="Household and Dependents">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Field label="Has Dependents" value={i.dependents} />
                    </div>
                    {Array.isArray(i.dependentDetails) && i.dependentDetails.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {i.dependentDetails.map((dependent, idx) => (
                          <Card key={idx}>
                            <p className="font-medium text-gray-800">Dependent {idx + 1}</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <Field
                                label="Relationship"
                                value={dependent.relation === 'other' ? dependent.relationOther : dependent.relation}
                              />
                              <Field label="Age" value={dependent.age} />
                              <Field label="Lives With Debtor" value={dependent.livesWith} />
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </Section>

                  {/* Housing */}
                  <Section title="Housing">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Owns Home" value={i.ownsHome} />
                      <Field label="Pays Rent" value={i.paysRent} />
                      <Field label="Rent Amount" value={i.rentAmount} />
                      <Field label="Home Value" value={i.homeValue} />
                      <Field label="Property Type" value={i.homePropertyType} />
                      <Field label="Ownership" value={i.homeOwnershipType} />
                      <Field label="Joint Ownership Detail" value={i.homeOwnershipDetail} />
                      <Field label="Has Mortgage" value={i.hasMortgage} />
                      <Field label="Mortgage Creditor" value={i.mortgageCreditor} />
                      <Field label="Mortgage Balance" value={i.mortgageBalance} />
                      <Field label="Mortgage Monthly Payment" value={i.mortgageMonthlyPayment} />
                      <Field label="Mortgage Current" value={i.mortgagePaymentStatus} />
                      <Field label="Mortgage Months Behind" value={i.mortgageMonthsBehind} />
                      <Field label="Has Other Real Estate" value={i.hasOtherRealEstate} />
                      <Field label="Spouse Has Other Real Estate" value={i.spouseHasOtherRealEstate} />
                    </div>
                    {[
                      i.hasSecondMortgage,
                      i.secondMortgageCreditor,
                      i.secondMortgageBalance,
                      i.secondMortgageMonthlyPayment,
                      i.secondMortgagePaymentStatus,
                      i.secondMortgageMonthsBehind,
                    ].some(hasDisplayValue) && (
                      <div className="mt-4">
                        <h5 className="text-md font-semibold text-gray-800 mb-2">Second Mortgage</h5>
                        <Card>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            <Field label="Has Second Mortgage" value={i.hasSecondMortgage} />
                            <Field label="Creditor" value={i.secondMortgageCreditor} />
                            <Field label="Balance" value={i.secondMortgageBalance} />
                            <Field label="Monthly Payment" value={i.secondMortgageMonthlyPayment} />
                            <Field label="Current" value={i.secondMortgagePaymentStatus} />
                            <Field label="Months Behind" value={i.secondMortgageMonthsBehind} />
                          </div>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.otherRealEstateProperties) && i.otherRealEstateProperties.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h5 className="text-md font-semibold text-gray-800">Other Real Estate</h5>
                        {i.otherRealEstateProperties.map((property, idx) => (
                          <IntakePropertyCard key={idx} property={property} title={`Property ${idx + 1}`} />
                        ))}
                      </div>
                    )}
                    {Array.isArray(i.spouseOtherRealEstateProperties) && i.spouseOtherRealEstateProperties.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h5 className="text-md font-semibold text-gray-800">Spouse Other Real Estate</h5>
                        {i.spouseOtherRealEstateProperties.map((property, idx) => (
                          <IntakePropertyCard key={idx} property={property} title={`Spouse Property ${idx + 1}`} />
                        ))}
                      </div>
                    )}
                  </Section>

                  {/* Employment */}
                  <Section title="Employment (Primary)">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Employed" value={i.employmentStatus} />
                      <Field label="Workplace" value={i.workPlace} />
                      <Field label="Job Title" value={i.jobTitle} />
                      <Field label="Pay Type" value={i.payType} />
                      <Field label="Hourly Rate" value={i.hourlyRate} />
                      <Field label="Yearly Salary" value={i.yearlySalary} />
                      <Field label="Hours Worked" value={i.hoursWorked} />
                      <Field label="Start Date" value={i.startDate} />
                      <Field label="Job Duration" value={i.jobDuration} />
                      <Field label="Net Profit" value={i.netProfit} />
                      <Field label="Payroll Deductions" value={i.deductions} />
                      <Field label="Has Second Job" value={i.secondJob} />
                      <Field label="Has Prior Employment" value={i.priorEmployment} />
                    </div>

                    {[
                      i.secondJob === 'yes' ? i.secondJob : '',
                      i.secondWorkPlace,
                      i.secondJobTitle,
                      i.secondPayType,
                      i.secondHourlyRate,
                      i.secondYearlySalary,
                      i.secondHoursWorked,
                      i.secondStartDate,
                      i.secondJobDuration,
                      i.secondNetProfit,
                    ].some(hasDisplayValue) && (
                      <>
                        <hr className="my-4 border-gray-300" />
                        <h5 className="text-md font-semibold text-gray-800 mb-2">Second Employment</h5>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Field label="Workplace" value={i.secondWorkPlace} />
                          <Field label="Job Title" value={i.secondJobTitle} />
                          <Field label="Pay Type" value={i.secondPayType} />
                          <Field label="Hourly Rate" value={i.secondHourlyRate} />
                          <Field label="Yearly Salary" value={i.secondYearlySalary} />
                          <Field label="Hours Worked" value={i.secondHoursWorked} />
                          <Field label="Start Date" value={i.secondStartDate} />
                          <Field label="Job Duration" value={i.secondJobDuration} />
                          <Field label="Net Profit" value={i.secondNetProfit} />
                        </div>
                      </>
                    )}

                    {[
                      i.priorEmployment === 'yes' ? i.priorEmployment : '',
                      i.priorWorkPlace,
                      i.priorJobTitle,
                      i.priorPayType,
                      i.priorHourlyRate,
                      i.priorYearlySalary,
                      i.priorHoursWorked,
                      i.priorStartDate,
                      i.priorLeavingDate,
                      i.priorJobDuration,
                      i.priorNetProfit,
                    ].some(hasDisplayValue) && (
                      <>
                        <hr className="my-4 border-gray-300" />
                        <h5 className="text-md font-semibold text-gray-800 mb-2">Prior Employment</h5>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Field label="Workplace" value={i.priorWorkPlace} />
                          <Field label="Job Title" value={i.priorJobTitle} />
                          <Field label="Pay Type" value={i.priorPayType} />
                          <Field label="Hourly Rate" value={i.priorHourlyRate} />
                          <Field label="Yearly Salary" value={i.priorYearlySalary} />
                          <Field label="Hours Worked" value={i.priorHoursWorked} />
                          <Field label="Start Date" value={i.priorStartDate} />
                          <Field label="Leaving Date" value={i.priorLeavingDate} />
                          <Field label="Job Duration" value={i.priorJobDuration} />
                          <Field label="Net Profit" value={i.priorNetProfit} />
                        </div>
                      </>
                    )}
                  </Section>

                  <Section title="Employment (Spouse)">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Employed" value={i.spouseEmploymentStatus} />
                      <Field label="Workplace" value={i.spouseWorkPlace} />
                      <Field label="Job Title" value={i.spouseJobTitle} />
                      <Field label="Pay Type" value={i.spousePayType} />
                      <Field label="Hourly Rate" value={i.spouseHourlyRate} />
                      <Field label="Yearly Salary" value={i.spouseYearlySalary} />
                      <Field label="Hours Worked" value={i.spouseHoursWorked} />
                      <Field label="Start Date" value={i.spouseStartDate} />
                      <Field label="Job Duration" value={i.spouseJobDuration} />
                      <Field label="Net Profit" value={i.spouseNetProfit} />
                      <Field label="Payroll Deductions" value={i.spouseDeductions} />
                      <Field label="Has Second Job" value={i.spouseSecondJob} />
                      <Field label="Has Prior Employment" value={i.spousePriorEmployment} />
                    </div>

                    {[
                      i.spouseSecondJob === 'yes' ? i.spouseSecondJob : '',
                      i.spouseSecondPayType,
                      i.spouseSecondHourlyRate,
                      i.spouseSecondYearlySalary,
                      i.spouseSecondHoursWorked,
                      i.spouseSecondWorkPlace,
                      i.spouseSecondJobTitle,
                      i.spouseSecondStartDate,
                      i.spouseSecondJobDuration,
                      i.spouseSecondNetProfit,
                    ].some(hasDisplayValue) && (
                      <>
                        <hr className="my-4 border-gray-300" />
                        <h5 className="text-md font-semibold text-gray-800 mb-2">Second Employment</h5>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Field label="Workplace" value={i.spouseSecondWorkPlace} />
                          <Field label="Job Title" value={i.spouseSecondJobTitle} />
                          <Field label="Pay Type" value={i.spouseSecondPayType} />
                          <Field label="Hourly Rate" value={i.spouseSecondHourlyRate} />
                          <Field label="Yearly Salary" value={i.spouseSecondYearlySalary} />
                          <Field label="Hours Worked" value={i.spouseSecondHoursWorked} />
                          <Field label="Start Date" value={i.spouseSecondStartDate} />
                          <Field label="Job Duration" value={i.spouseSecondJobDuration} />
                          <Field label="Net Profit" value={i.spouseSecondNetProfit} />
                        </div>
                      </>
                    )}

                    {[
                      i.spousePriorEmployment === 'yes' ? i.spousePriorEmployment : '',
                      i.spousePriorWorkPlace,
                      i.spousePriorJobTitle,
                      i.spousePriorPayType,
                      i.spousePriorHourlyRate,
                      i.spousePriorYearlySalary,
                      i.spousePriorHoursWorked,
                      i.spousePriorStartDate,
                      i.spousePriorLeavingDate,
                      i.spousePriorJobDuration,
                      i.spousePriorNetProfit,
                    ].some(hasDisplayValue) && (
                      <>
                        <hr className="my-4 border-gray-300" />
                        <h5 className="text-md font-semibold text-gray-800 mb-2">Prior Employment</h5>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Field label="Workplace" value={i.spousePriorWorkPlace} />
                          <Field label="Job Title" value={i.spousePriorJobTitle} />
                          <Field label="Pay Type" value={i.spousePriorPayType} />
                          <Field label="Hourly Rate" value={i.spousePriorHourlyRate} />
                          <Field label="Yearly Salary" value={i.spousePriorYearlySalary} />
                          <Field label="Hours Worked" value={i.spousePriorHoursWorked} />
                          <Field label="Start Date" value={i.spousePriorStartDate} />
                          <Field label="Leaving Date" value={i.spousePriorLeavingDate} />
                          <Field label="Job Duration" value={i.spousePriorJobDuration} />
                          <Field label="Net Profit" value={i.spousePriorNetProfit} />
                        </div>
                      </>
                    )}
                  </Section>

                  <Section title="Support Obligations">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Pays Child Support" value={i.childSupport} />
                      <Field label="Child Support Amount" value={i.childSupportAmount} />
                      <Field label="Deducted from Wages" value={i.childSupportDeducted} />
                      <Field label="Has Child Support Arrearage" value={i.childSupportArrearage} />
                      <Field label="Child Support Arrearage Amount" value={i.childSupportArrearageAmount} />
                      <Field label="Pays Alimony" value={i.alimony} />
                      <Field label="Alimony Amount" value={i.alimonyAmount} />
                    </div>
                    {i.maritalStatus === 'married' && (
                      <>
                        <hr className="my-4 border-gray-300" />
                        <h5 className="text-md font-semibold text-gray-800 mb-2">Spouse</h5>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <Field label="Pays Child Support" value={i.spouseChildSupport} />
                          <Field label="Child Support Amount" value={i.spouseChildSupportAmount} />
                          <Field label="Deducted from Wages" value={i.spouseChildSupportDeducted} />
                          <Field label="Has Child Support Arrearage" value={i.spouseChildSupportArrearage} />
                          <Field label="Child Support Arrearage Amount" value={i.spouseChildSupportArrearageAmount} />
                          <Field label="Pays Alimony" value={i.spouseAlimony} />
                          <Field label="Alimony Amount" value={i.spouseAlimonyAmount} />
                        </div>
                      </>
                    )}
                  </Section>

                  {/* Fixed Income */}
                  <Section title="Fixed Income">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Has Fixed Income" value={i.hasFixedIncome} />
                      <Field label="Sources" value={Array.isArray(i.fixedIncomeSources) && i.fixedIncomeSources.length ? i.fixedIncomeSources.join(', ') : ''} />
                    </div>
                    {i.fixedIncomeDetails && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Fixed Income Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.fixedIncomeDetails && typeof i.fixedIncomeDetails === 'object' &&
                              Object.entries(i.fixedIncomeDetails).map(([k, v]) => {
                                const isObj = v && typeof v === 'object';
                                const amount = isObj ? v.amount : v;
                                const desc = isObj ? v.description : null;
                                return (
                                  <li key={k}>
                                    {k}: {'$'}{amount}
                                    {desc ? ` — ${desc}` : ''}
                                  </li>
                                );
                              })}
                          </ul>
                        </Card>
                      </div>
                    )}
                  </Section>

                  <Section title="Fixed Income (Spouse)">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Has Fixed Income" value={i.spouseHasFixedIncome} />
                      <Field label="Sources" value={Array.isArray(i.spouseFixedIncomeSources) && i.spouseFixedIncomeSources.length ? i.spouseFixedIncomeSources.join(', ') : ''} />
                    </div>
                    {i.spouseFixedIncomeDetails && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Spouse Fixed Income Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.spouseFixedIncomeDetails && typeof i.spouseFixedIncomeDetails === 'object' &&
                              Object.entries(i.spouseFixedIncomeDetails).map(([k, v]) => {
                                const isObj = v && typeof v === 'object';
                                const amount = isObj ? v.amount : v;
                                const desc = isObj ? v.description : null;
                                return (
                                  <li key={k}>
                                    {k}: {'$'}{amount}
                                    {desc ? ` — ${desc}` : ''}
                                  </li>
                                );
                              })}
                          </ul>
                        </Card>
                      </div>
                    )}
                  </Section>

                  {/* Vehicles */}
                  <Section title="Vehicle Overview">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Field label="Has Vehicle" value={i.hasVehicle} />
                      <Field label="Spouse Has Vehicle" value={i.spouseHasVehicle} />
                      <Field label="Vehicle Insurance" value={i.vehicleInsurance} />
                    </div>
                  </Section>

                  {Array.isArray(i.vehicles) && i.vehicles.length > 0 && (
                    <Section title="Vehicles (from Intake)">
                      <div className="space-y-2">
                        {i.vehicles.map((v, idx) => (
                          <Card key={idx}>
                            <p className="font-medium text-gray-800">{[v.year, v.make, v.model].filter(Boolean).join(' ') || `Vehicle ${idx+1}`}</p>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-gray-700 mt-1">
                              <Field label="Miles" value={v.miles} />
                              <Field label="Ownership" value={v.ownership} />
                              <Field label="Loan Status" value={v.loan} />
                              <Field label="Creditor" value={v.creditor} />
                              <Field label="Loan Balance" value={v.loanBalance} />
                              <Field label="Monthly Payment" value={v.monthlyPayment} />
                              <Field label="Current on Payments" value={v.currentOnPayments} />
                              <Field label="Months Behind" value={v.monthsBehind} />
                              <Field label="Intent" value={v.keep} />
                            </div>
                          </Card>
                        ))}
                      </div>
                    </Section>
                  )}

                  {Array.isArray(i.spouseVehicles) && i.spouseVehicles.length > 0 && (
                    <Section title="Spouse Vehicles (from Intake)">
                      <div className="space-y-2">
                        {i.spouseVehicles.map((v, idx) => (
                          <Card key={idx}>
                            <p className="font-medium text-gray-800">{[v.year, v.make, v.model].filter(Boolean).join(' ') || `Vehicle ${idx+1}`}</p>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-gray-700 mt-1">
                              <Field label="Miles" value={v.miles} />
                              <Field label="Ownership" value={v.ownership} />
                              <Field label="Loan Status" value={v.loan} />
                              <Field label="Creditor" value={v.creditor} />
                              <Field label="Loan Balance" value={v.loanBalance} />
                              <Field label="Monthly Payment" value={v.monthlyPayment} />
                              <Field label="Current on Payments" value={v.currentOnPayments} />
                              <Field label="Months Behind" value={v.monthsBehind} />
                              <Field label="Intent" value={v.keep} />
                            </div>
                          </Card>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* Debts */}
                  <Section title="Debts (Self-reported)">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Credit Cards" value={i.creditCardDebt} />
                      <Field label="Medical Debt" value={i.medicalDebt} />
                      <Field label="Personal Loans" value={i.personalLoanDebt} />
                      <Field label="Back Taxes" value={i.backTaxDebt} />
                      <Field label="Student Loans" value={i.studentLoanDebt} />
                      <Field label="Other Debt" value={i.otherDebt} />
                    </div>
                    {Array.isArray(i.creditCardDebtDetails) && i.creditCardDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Credit Card Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.creditCardDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.debtBalance}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.personalLoanDebtDetails) && i.personalLoanDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Personal Loan Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.personalLoanDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.debtBalance}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.medicalDebtDetails) && i.medicalDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Medical Debt Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.medicalDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.amountOwed}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.studentLoanDebtDetails) && i.studentLoanDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Student Loan Debt Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.studentLoanDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.debtBalance}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.backTaxDebtDetails) && i.backTaxDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Back Tax Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.backTaxDebtDetails.map((d, k) => (
                              <li key={k}>{d.taxType} {d.year}: ${d.amount}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.otherDebtDetails) && i.otherDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Other Debt Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.otherDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor} {d.description}: ${d.amountOwed}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    <hr className="my-4 border-gray-300" />
                    <h5 className="text-md font-semibold text-gray-800 mb-2">Spouse</h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Field label="Credit Cards" value={i.spouseCreditCardDebt} />
                      <Field label="Medical Debt" value={i.spouseMedicalDebt} />
                      <Field label="Personal Loans" value={i.spousePersonalLoanDebt} />
                      <Field label="Back Taxes" value={i.spouseBackTaxDebt} />
                      <Field label="Student Loans" value={i.spouseStudentLoanDebt} />
                      <Field label="Other Debt" value={i.spouseOtherDebt} />
                    </div>
                    {Array.isArray(i.spouseCreditCardDebtDetails) && i.spouseCreditCardDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Spouse Credit Card Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.spouseCreditCardDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.debtBalance}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.spousePersonalLoanDebtDetails) && i.spousePersonalLoanDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Spouse Personal Loan Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.spousePersonalLoanDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.debtBalance}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.spouseMedicalDebtDetails) && i.spouseMedicalDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Spouse Medical Debt Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.spouseMedicalDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.amountOwed}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.spouseStudentLoanDebtDetails) && i.spouseStudentLoanDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Student Loan Debt Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.spouseStudentLoanDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor}: ${d.debtBalance}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.spouseBackTaxDebtDetails) && i.spouseBackTaxDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Spouse Back Tax Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.spouseBackTaxDebtDetails.map((d, k) => (
                              <li key={k}>{d.taxType} {d.year}: ${d.amount}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                    {Array.isArray(i.spouseOtherDebtDetails) && i.spouseOtherDebtDetails.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 font-medium">Other Debt Details:</p>
                        <Card>
                          <ul className="list-disc list-inside text-gray-700">
                            {i.spouseOtherDebtDetails.map((d, k) => (
                              <li key={k}>{d.creditor} {d.description}: ${d.amountOwed}</li>
                            ))}
                          </ul>
                        </Card>
                      </div>
                    )}
                  </Section>

                  {/* Census & Geocoding */}
                  {(i.censusTract || i.censusGeocode) && (
                    <Section title="Census & Geocoding">
                      <Card>
                        {i.censusTract && (
                          <div className="text-gray-700 mb-2">
                            <p className="font-medium">Census Tract:</p>
                            <p>{i.censusTract.NAME} ({i.censusTract.GEOID})</p>
                            <p>Center: {i.censusTract.CENTLAT}, {i.censusTract.CENTLON}</p>
                          </div>
                        )}
                        {i.censusGeocode?.result?.addressMatches?.length > 0 && (
                          <div className="text-gray-700">
                            <p className="font-medium">Matched Address:</p>
                            <p>{i.censusGeocode.result.addressMatches[0].matchedAddress}</p>
                            <p>Coords: {i.censusGeocode.result.addressMatches[0].coordinates.y}, {i.censusGeocode.result.addressMatches[0].coordinates.x}</p>
                          </div>
                        )}
                      </Card>
                    </Section>
                  )}

                  {/* Consultation Result */}
                  {i.consultationResult && (
                    <Section title="Consultation Result">
                      <div className="text-gray-700">
                        <Field label="Recommendation" value={i.consultationResult.recommendation} />
                        {i.consultationResult.fees && (
                          <div className="mt-2">
                            <p className="font-medium">Fees:</p>
                            <Card>
                              <ul className="list-disc list-inside">
                                {Object.entries(i.consultationResult.fees).map(([k,v]) => (
                                  <li key={k}>{k.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase())}: ${v}</li>
                                ))}
                              </ul>
                            </Card>
                          </div>
                        )}
                        {i.consultationResult.computedValues && (
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            {['plan36','plan60'].map(p => (
                              i.consultationResult.computedValues[p] ? (
                                <Card key={p}>
                                  <p className="font-medium">{p.toUpperCase()}</p>
                                  <Field label="Plan Length" value={i.consultationResult.computedValues[p].planLength} />
                                  <Field label="Monthly Payment" value={i.consultationResult.computedValues[p].monthlyPayment} />
                                  <Field label="Gross Plan Funded" value={i.consultationResult.computedValues[p].grossPlanFunded} />
                                  <Field label="Unsecured Total" value={i.consultationResult.computedValues[p].unsecuredTotal} />
                                </Card>
                              ) : null
                            ))}
                          </div>
                        )}
                      </div>
                    </Section>
                  )}
                </div>
              );
            })()}
          </div>
        </details>
      )}

      {/* Edit Income Modal */}
      {showEditIncome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-lg w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Income</h2>
            {/* Tabbed Income Editing */}
            <Tab.Group>
              <Tab.List className="flex space-x-2 border-b mb-4">
                {['Employments','Fixed Income','Business Income'].map(tab => (
                  <Tab key={tab} className={({ selected }) =>
                    `px-3 py-1 rounded-t ${selected ? 'bg-white border-t border-l border-r' : 'bg-gray-200'}`
                  }>
                    {tab}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                <Tab.Panel>
  {editingIncomeData.map((debtor, di) => (
    <div key={di} className="mb-6 border p-4 rounded">
      <h4 className="font-semibold mb-4">
        {[debtor.first_name, debtor.second_name, debtor.last_name].filter(Boolean).join(' ')} — Employments
      </h4>

      {/* For each employment record: */}
      {debtor.employments.map((emp, ei) => (
        <div key={ei} className="mb-4 border p-3 rounded">
          {/* Row 2: Employer, Occupation, Start Date, End Date, Length */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-2">
            {/* Employer */}
            <div>
              <label className="block text-sm font-medium">Employer</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm lg:col-span-3"
                value={emp.employer || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Occupation */}
            <div>
              <label className="block text-sm font-medium">Occupation</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm lg:col-span-2"
                value={emp.occupation || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].occupation = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium">Start Date</label>
              <input
                type="date"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.start_date || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].start_date = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* End Date */}
            <div>
              <label className="block text-sm font-medium">End Date</label>
              <input
                type="date"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.end_date || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].end_date = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Length */}
            <div>
              <label className="block text-sm font-medium">Length of Employment</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                maxLength={50}
                placeholder="e.g., 1 year or 3 months"
                value={emp.length_of_employment || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].length_of_employment = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          {/* Row 1: Street, Street 2, City, State, ZIP */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-2">
            {/* Street */}
            <div>
              <label className="block text-sm font-medium">Street</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.employer_street || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_street = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Street 2 */}
            <div>
              <label className="block text-sm font-medium">Street 2</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.employer_street2 || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_street2 = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* City */}
            <div>
              <label className="block text-sm font-medium">City</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.employer_city || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_city = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* State */}
            <div>
              <label className="block text-sm font-medium">State</label>
              <input
                type="text"
                maxLength={2}
                className="mt-1 block w-full border rounded px-1 py-1 text-sm w-16"
                value={emp.employer_state || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_state = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* ZIP */}
            <div>
              <label className="block text-sm font-medium">ZIP</label>
              <input
                type="text"
                maxLength={5}
                className="mt-1 block w-full border rounded px-1 py-1 text-sm w-16"
                value={emp.employer_zip || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_zip = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          {/* Row 3: Pay Type, Pay Schedule, Rate, Units */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            {/* Pay Type */}
            <div>
              <label className="block text-sm font-medium">Pay Type</label>
              <select className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_type || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_type = e.target.value;
                  setEditingIncomeData(upd);
                }}
              >
                <option value="" disabled>Select</option>
                <option value="hourly">Hourly</option>
                <option value="salary">Salary</option>
                <option value="self-employed">Self-Employed</option>
              </select>
            </div>
            {/* Pay Schedule */}
            <div>
              <label className="block text-sm font-medium">Pay Schedule</label>
              <select className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_schedule || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_schedule = e.target.value;
                  setEditingIncomeData(upd);
                }}
              >
                <option value="" disabled>Select</option>
                <option value="hourly">Hourly</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="semimonthly">Semimonthly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            {/* Pay Rate */}
            <div>
              <label className="block text-sm font-medium">Rate</label>
              <input type="number" step="0.01" className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_rate || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_rate = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Pay Units */}
            <div>
              <label className="block text-sm font-medium">Units</label>
              <input type="text" className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_units || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_units = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          <label className="mt-3 flex items-start gap-2 rounded border border-blue-200 bg-blue-50 p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={emp.has_payment_advices !== false}
              onChange={e => {
                const upd = [...editingIncomeData];
                upd[di].employments[ei].has_payment_advices = e.target.checked;
                setEditingIncomeData(upd);
              }}
            />
            <span>
              <span className="block text-sm font-medium">Has payment advices/pay stubs</span>
              <span className="block text-xs text-gray-600">
                This controls the debtor’s answer on the Payment Advices PDF.
              </span>
            </span>
          </label>

          {/* Deductions Section */}
          <div className="mt-4">
            <h5 className="font-medium mb-2">Deductions</h5>
            {emp.deductions.map((det, di2) => (
              <div key={di2} className="flex space-x-2 items-end mb-2">
                <select
                  className="border rounded px-2 py-1"
                  value={det.deduction_type}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].employments[ei].deductions[di2].deduction_type = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                >
                  <option value="" disabled>Type</option>
                  {['taxes','mandatory_retirement','voluntary_retirement','retirement_loan','insurance','dso','union_dues','other'].map(opt => (
                    <option key={opt} value={opt}>{opt.replace(/_/g,' ')}</option>
                  ))}
                </select>
                {det.deduction_type === 'other' && (
                  <input
                    type="text"
                    placeholder="Description"
                    className="border rounded px-2 py-1 flex-1"
                    value={det.other_description || ''}
                    onChange={e => {
                      const upd = [...editingIncomeData];
                      upd[di].employments[ei].deductions[di2].other_description = e.target.value;
                      setEditingIncomeData(upd);
                    }}
                  />
                )}
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  className="w-24 border rounded px-2 py-1"
                  value={det.amount || ''}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].employments[ei].deductions[di2].amount = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                />
                <button
                  className="text-red-600"
                  onClick={() => {
                    const upd = [...editingIncomeData];
                    upd[di].employments[ei].deductions = upd[di].employments[ei].deductions.filter((_, k) => k !== di2);
                    setEditingIncomeData(upd);
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="text-blue-600 text-sm"
              onClick={() => {
                const upd = [...editingIncomeData];
                upd[di].employments[ei].deductions.push({ deduction_type:'', other_description:'', amount:'' });
                setEditingIncomeData(upd);
              }}
            >
              + Add Deduction
            </button>
          </div>

          {/* Remove Employment */}
          <button
            className="mt-3 text-red-600"
            onClick={() => {
              const upd = [...editingIncomeData];
              upd[di].employments = upd[di].employments.filter((_, k) => k !== ei);
              setEditingIncomeData(upd);
            }}
          >
            Remove Employment
          </button>
        </div>
      ))}

      {/* Add Employment */}
      <button
        className="text-blue-600 text-sm"
        onClick={() => {
          const upd = [...editingIncomeData];
          upd[di].employments.push({
            employer:'', occupation:'', employer_street:'', employer_street2:'',
            employer_city:'', employer_state:'', employer_zip:'',
            length_of_employment:'', start_date:'', end_date:'',
            pay_type:'', pay_schedule:'', pay_units:'', pay_rate:'',
            has_payment_advices:true, deductions:[]
          });
          setEditingIncomeData(upd);
        }}
      >
        + Add Employment
      </button>
    </div>
  ))}
                </Tab.Panel>
                {/* Fixed Income Tab Panel */}
                <Tab.Panel>
                  <div className="space-y-3">
                    {editingIncomeData.map((debtorData, idx) => {
                      const fullName = [debtorData.first_name, debtorData.second_name, debtorData.last_name]
                        .filter(Boolean).join(' ');
                      return (
                        <div key={idx} className="border-b pb-4">
                          <h4 className="text-md font-semibold mb-2">{fullName} — Fixed Income</h4>

                          {/* Single‐value fixed incomes in a responsive grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                            {[
                              'interest_dividend_income',
                              'family_support_payments',
                              'unemployment_income',
                              'social_security_income',
                              'pension_income'
                            ].map(field => (
                              <div key={field}>
                                <label className="block text-sm font-medium text-gray-700 capitalize">
                                  {field.replace(/_/g,' ')}
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="mt-1 block w-full border rounded px-2 py-1"
                                  value={debtorData[field] ?? ''}
                                  onChange={e => {
                                    const val = e.target.value;
                                    const updated = editingIncomeData.map((d,i) =>
                                      i===idx ? {...d, [field]: val} : d
                                    );
                                    setEditingIncomeData(updated);
                                  }}
                                />
                              </div>
                            ))}
                          </div>

                          {/* Combine list categories into one row */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Government Assistance panel */}
                            <div>
                              <h5 className="font-medium mb-2">Government Assistance</h5>
                              {debtorData.gov_assistances.map((item,j) => (
                                <div key={j} className="grid grid-cols-6 gap-2 items-end mb-2">
                                  <input
                                    type="text"
                                    placeholder="Program Name"
                                    className="col-span-3 border rounded px-2 py-1"
                                    value={item.name}
                                    onChange={e => {
                                      const list = [...debtorData.gov_assistances];
                                      list[j].name = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i) =>
                                        i===idx ? {...d, gov_assistances:list} : d
                                      ));
                                    }}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Amount"
                                    className="col-span-2 border rounded px-2 py-1"
                                    value={item.amount}
                                    onChange={e => {
                                      const list = [...debtorData.gov_assistances];
                                      list[j].amount = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i) =>
                                        i===idx ? {...d, gov_assistances:list} : d
                                      ));
                                    }}
                                  />
                                  <button
                                    className="text-red-600"
                                    onClick={() => {
                                      const list = debtorData.gov_assistances.filter((_,k)=>k!==j);
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, gov_assistances:list} : d
                                      ));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                className="text-blue-600 text-sm"
                                onClick={() => {
                                  const list = [...debtorData.gov_assistances, {name:'',amount:''}];
                                  setEditingIncomeData(editingIncomeData.map((d,i)=>
                                    i===idx ? {...d, gov_assistances:list} : d
                                  ));
                                }}
                              >
                                + Add Assistance
                              </button>
                            </div>
                            {/* Other Income panel */}
                            <div>
                              <h5 className="font-medium mb-2">Other Income</h5>
                              {debtorData.other_income_items.map((item,j) => (
                                <div key={j} className="grid grid-cols-6 gap-2 items-end mb-2">
                                  <input
                                    type="text"
                                    placeholder="Description"
                                    className="col-span-3 border rounded px-2 py-1"
                                    value={item.name}
                                    onChange={e => {
                                      const list = [...debtorData.other_income_items];
                                      list[j].name = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, other_income_items:list} : d
                                      ));
                                    }}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Amount"
                                    className="col-span-2 border rounded px-2 py-1"
                                    value={item.amount}
                                    onChange={e => {
                                      const list = [...debtorData.other_income_items];
                                      list[j].amount = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, other_income_items:list} : d
                                      ));
                                    }}
                                  />
                                  <button
                                    className="text-red-600"
                                    onClick={() => {
                                      const list = debtorData.other_income_items.filter((_,k)=>k!==j);
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, other_income_items:list} : d
                                      ));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                className="text-blue-600 text-sm"
                                onClick={() => {
                                  const list = [...debtorData.other_income_items, {name:'',amount:''}];
                                  setEditingIncomeData(editingIncomeData.map((d,i)=>
                                    i===idx ? {...d, other_income_items:list} : d
                                  ));
                                }}
                              >
                                + Add Other Income
                              </button>
                            </div>
                            {/* Expense Contributions panel */}
                            <div>
                              <h5 className="font-medium mb-2">Expense Contributions</h5>
                              {debtorData.expense_contributions.map((item,j) => (
                                <div key={j} className="grid grid-cols-6 gap-2 items-end mb-2">
                                  <input
                                    type="text"
                                    placeholder="Source"
                                    className="col-span-3 border rounded px-2 py-1"
                                    value={item.name}
                                    onChange={e => {
                                      const list = [...debtorData.expense_contributions];
                                      list[j].name = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, expense_contributions:list} : d
                                      ));
                                    }}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Amount"
                                    className="col-span-2 border rounded px-2 py-1"
                                    value={item.amount}
                                    onChange={e => {
                                      const list = [...debtorData.expense_contributions];
                                      list[j].amount = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, expense_contributions:list} : d
                                      ));
                                    }}
                                  />
                                  <button
                                    className="text-red-600"
                                    onClick={() => {
                                      const list = debtorData.expense_contributions.filter((_,k)=>k!==j);
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, expense_contributions:list} : d
                                      ));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                className="text-blue-600 text-sm"
                                onClick={() => {
                                  const list = [...debtorData.expense_contributions, {name:'',amount:''}];
                                  setEditingIncomeData(editingIncomeData.map((d,i)=>
                                    i===idx ? {...d, expense_contributions:list} : d
                                  ));
                                }}
                              >
                                + Add Contribution
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Tab.Panel>
                <Tab.Panel>
  {editingIncomeData.map((debtor, di) => (
    <div key={di} className="mb-6 border p-4 rounded">
      <h4 className="font-semibold mb-4">
        {[debtor.first_name, debtor.second_name, debtor.last_name]
          .filter(Boolean)
          .join(' ')} — Business Income
      </h4>

      {/* Business Income Records */}
      {debtor.business_incomes.map((biz, bi) => (
        <div key={biz.id || bi} className="mb-6 border p-3 rounded">
          {/* Name & Gross Revenue */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            <div>
              <label className="block text-sm font-medium">Business Name</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-2 py-1 text-sm"
                value={biz.name || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].business_incomes[bi].name = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Gross Revenue</label>
              <input
                type="number"
                step="0.01"
                className="mt-1 block w-full border rounded px-2 py-1 text-sm"
                value={biz.gross_revenue || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].business_incomes[bi].gross_revenue = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          {/* Expenses */}
          <div className="mb-4">
            <h5 className="font-medium mb-2">Expenses</h5>
            {biz.expenses.map((exp, ei) => (
              <div key={exp.id || ei} className="grid grid-cols-6 gap-2 items-end mb-2">
                <input
                  type="text"
                  placeholder="Expense Name"
                  className="col-span-4 border rounded px-2 py-1 text-sm"
                  value={exp.expense_name || ''}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].business_incomes[bi].expenses[ei].expense_name = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  className="col-span-1 border rounded px-2 py-1 text-sm"
                  value={exp.amount || ''}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].business_incomes[bi].expenses[ei].amount = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                />
                <button
                  className="text-red-600"
                  onClick={() => {
                    const upd = [...editingIncomeData];
                    upd[di].business_incomes[bi].expenses = upd[di]
                      .business_incomes[bi]
                      .expenses.filter((_, idx) => idx !== ei);
                    setEditingIncomeData(upd);
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="text-blue-600 text-sm"
              onClick={() => {
                const upd = [...editingIncomeData];
                upd[di].business_incomes[bi].expenses.push({ expense_name: '', amount: '' });
                setEditingIncomeData(upd);
              }}
            >
              + Add Expense
            </button>
          </div>

          {/* Remove Business Income */}
          <button
            className="mt-2 text-red-600"
            onClick={() => {
              const upd = [...editingIncomeData];
              upd[di].business_incomes = upd[di].business_incomes.filter((_, idx) => idx !== bi);
              setEditingIncomeData(upd);
            }}
          >
            Remove Business
          </button>
        </div>
      ))}

      {/* Add Business Income */}
      <button
        className="text-blue-600 text-sm"
        onClick={() => {
          const upd = [...editingIncomeData];
          upd[di].business_incomes.push({ name: '', gross_revenue: '', expenses: [] });
          setEditingIncomeData(upd);
        }}
      >
        + Add Business Income
      </button>
    </div>
  ))}
  </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={async () => {
                  try {
                    // Send PUT for each debtor's employments, fixed income, and business incomes
                    const updatedDebtors = [];
                    for (const debtorData of editingIncomeData) {
                      // First, sync employments
                      const empResp = await fetch(
                        `${API}/case/${caseId}/debtors/${debtorData.id}/employments/`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({ employments: debtorData.employments }),
                        }
                      );
                      if (!empResp.ok) throw new Error('Failed to save employments for ' + debtorData.id);
                      // then sync fixed income
                      const resp = await fetch(
                        `${API}/case/${caseId}/debtors/${debtorData.id}/income/`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({
                            interest_dividend_income: debtorData.interest_dividend_income,
                            family_support_payments: debtorData.family_support_payments,
                            unemployment_income: debtorData.unemployment_income,
                            social_security_income: debtorData.social_security_income,
                            pension_income: debtorData.pension_income,
                            gov_assistances: debtorData.gov_assistances,
                            other_income_items: debtorData.other_income_items,
                            expense_contributions: debtorData.expense_contributions,
                          }),
                        }
                      );
                      // then sync business incomes
                      const bizResp = await fetch(
                        `${API}/case/${caseId}/debtors/${debtorData.id}/business-incomes/`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({ business_incomes: debtorData.business_incomes }),
                        }
                      );
                      if (!resp.ok) throw new Error('Failed to save income for ' + debtorData.id);
                      if (!bizResp.ok) throw new Error('Failed to save business income for ' + debtorData.id);
                      const updated = await resp.json();
                      updatedDebtors.push(updated);
                    }
                    // Merge updated debtor info into caseData
                    applyCaseData({
                      ...caseData,
                      debtors: caseData.debtors.map(d => {
                        const updated = updatedDebtors.find(u => u.id === d.id);
                        return updated || d;
                      }),
                    });
                    setShowEditIncome(false);
                  } catch (err) {
                    console.error(err);
                    alert('Failed to save income: ' + err.message);
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded"
                ref={saveIncomeRef}
              >
                Save
              </button>
              <button
                onClick={() => setShowEditIncome(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Case Modal */}
    {showEditCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-lg w-full max-w-3xl p-6 md:p-5 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Case</h2>
            <div className="space-y-4">
              {/* Core details in a wide responsive grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingCaseData.status}
                    onChange={e => setEditingCaseData({ ...editingCaseData, status: e.target.value })}
                  >
                    <option value="prospect">Prospect</option>
                    <option value="retained">Retained</option>
                    <option value="filed">Case Filed</option>
                    <option value="confirmed">Case Confirmed</option>
                    <option value="discharged">Case Discharged</option>
                    <option value="dismissed">Case Dismissed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                {/* Joint */}
                <div className="flex items-end">
                  <label className="inline-flex items-center mt-6">
                    <input
                      type="checkbox"
                      checked={editingCaseData.joint}
                      onChange={e => setEditingCaseData({ ...editingCaseData, joint: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Joint Filing</span>
                  </label>
                </div>

                {/* Chapter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Chapter</label>
                  <select
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingCaseData.chapter}
                    onChange={e => setEditingCaseData({ ...editingCaseData, chapter: e.target.value })}
                  >
                    <option value="7">7</option>
                    <option value="13">13</option>
                  </select>
                </div>
              </div>

              {/* Fees & Amounts */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-2">Fees & Amounts</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['filing_fee_amount','filing_fees_paid','filing_fees_receipted','pre_filing_fees','total_attorney_fees','fees_paid_prior'].map(field => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700">
                        {field.replace(/_/g,' ')}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingCaseData[field] ?? ''}
                        onChange={e => setEditingCaseData({
                          ...editingCaseData,
                          [field]: e.target.value
                        })}
                      />
                    </div>
                  ))}
                  {/* Case Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Case Number</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingCaseData.case_number || ''}
                      onChange={e => setEditingCaseData({ ...editingCaseData, case_number: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Fee Sources */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-2">Fee Sources</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Fees Source */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fees Source</label>
                    <select
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingCaseData.fees_source || 'debtor'}
                      onChange={e => setEditingCaseData({ ...editingCaseData, fees_source: e.target.value })}
                    >
                      <option value="debtor">Debtor</option>
                      <option value="other">Other</option>
                    </select>
                    {(editingCaseData.fees_source === 'other') && (
                      <textarea
                        className="mt-2 block w-full border rounded px-2 py-1"
                        rows={2}
                        placeholder="If 'other', describe the fees source"
                        value={editingCaseData.fees_source_description || ''}
                        onChange={e => setEditingCaseData({ ...editingCaseData, fees_source_description: e.target.value })}
                      />
                    )}
                  </div>

                  {/* Fees To Be Paid Source */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fees To Be Paid Source</label>
                    <select
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingCaseData.fees_to_be_paid_source || 'debtor'}
                      onChange={e => setEditingCaseData({ ...editingCaseData, fees_to_be_paid_source: e.target.value })}
                    >
                      <option value="debtor">Debtor</option>
                      <option value="other">Other</option>
                    </select>
                    {(editingCaseData.fees_to_be_paid_source === 'other') && (
                      <textarea
                        className="mt-2 block w-full border rounded px-2 py-1"
                        rows={2}
                        placeholder="If 'other', describe the fees-to-be-paid source"
                        value={editingCaseData.fees_to_be_paid_description || ''}
                        onChange={e => setEditingCaseData({ ...editingCaseData, fees_to_be_paid_description: e.target.value })}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Dates & Deadlines */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-2">Dates & Deadlines</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date Filed</label>
                    <input
                      type="date"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingCaseData.date_filed || ''}
                      onChange={e => setEditingCaseData({ ...editingCaseData, date_filed: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Meeting Date</label>
                    <input
                      type="datetime-local"
                      step="1800"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingCaseData.meeting_date ? editingCaseData.meeting_date.slice(0,16) : ''}
                      onChange={e => setEditingCaseData({ ...editingCaseData, meeting_date: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                  <input
                    id="admin_hearing"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!editingCaseData?.admin_hearing}
                    onChange={(e) =>
                      setEditingCaseData({
                        ...editingCaseData,
                        admin_hearing: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="admin_hearing" className="text-sm font-medium text-gray-700">
                    Admin hearing
                  </label>
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Signing Date</label>
                    <input
                      type="datetime-local"
                      step="1800"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingCaseData.case_signing_date ? editingCaseData.case_signing_date.slice(0,16) : ''}
                      onChange={e => setEditingCaseData({ ...editingCaseData, case_signing_date: e.target.value })}
                    />
                  </div>
                  {['reaffirmation_deadline','proof_of_claims_deadline','financial_management_deadline','confirmation_date'].map(field => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700">{field.replace(/_/g,' ')}</label>
                      <input
                        type="date"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingCaseData[field] || ''}
                        onChange={e => setEditingCaseData({ ...editingCaseData, [field]: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Trustee */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Trustee</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingCaseData.trustee || ''}
                    onChange={e => setEditingCaseData({ ...editingCaseData, trustee: e.target.value })}
                  />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center mt-6">
                    <input
                      type="checkbox"
                      checked={!!editingCaseData.trustee_docs}
                      onChange={e => setEditingCaseData({ ...editingCaseData, trustee_docs: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Trustee Docs Complete</span>
                  </label>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center mt-6">
                    <input
                      type="checkbox"
                      checked={!!editingCaseData.service_docs}
                      onChange={e => setEditingCaseData({ ...editingCaseData, service_docs: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Service Docs Complete</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-4">
              <button
                onClick={async () => {
                  try {
                    const resp = await fetch(`${API}/case/${caseId}/`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify(editingCaseData),
                    });
                    if (!resp.ok) throw new Error('Update failed');
                    const updated = await resp.json();
                    applyCaseData(updated);
                    setEditingCaseData(updated)
                    setShowEditCase(false);
                  } catch (err) {
                    console.error(err);
                    alert('Failed to update case');
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded"
                ref={saveCaseRef}
              >
                Save
              </button>
              <button onClick={() => setShowEditCase(false)} className="px-4 py-2 bg-gray-300 rounded">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Debtor Modal */}
      {showEditDebtor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-lg w-full max-w-4xl p-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Debtor</h2>
            <div className="space-y-2 grid grid-cols-1 gap-4 lg:space-y-2">
              {/* Filing/Primary toggles */}
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingDebtor.is_primary}
                    onChange={e => setEditingDebtor({ ...editingDebtor, is_primary: e.target.checked })}
                    className="mr-2"
                  />
                  Primary Debtor
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingDebtor.is_filing}
                    onChange={e => setEditingDebtor({ ...editingDebtor, is_filing: e.target.checked })}
                    className="mr-2"
                  />
                  Filing Debtor
                </label>
              </div>
              {/* Name fields on one line */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {['first_name','second_name','last_name','suffix'].map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700">
                      {field.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingDebtor[field]||''}
                      onChange={e => setEditingDebtor({ ...editingDebtor, [field]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              {/* Contact row: DOB, Marital Status, Email, Phone, Email Notice */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                {/* DOB */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">DOB</label>
                  <input
                    type="date"
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingDebtor.dob||''}
                    onChange={e => setEditingDebtor({ ...editingDebtor, dob: e.target.value })}
                  />
                </div>
                {/* Marital Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Marital Status</label>
                  <select
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingDebtor.marital_status || ''}
                    onChange={e => setEditingDebtor({ ...editingDebtor, marital_status: e.target.value })}
                  >
                    <option value="" disabled>Select</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                    <option value="separated">Separated</option>
                  </select>
                </div>
                {/* Email */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingDebtor.email||''}
                    onChange={e => setEditingDebtor({ ...editingDebtor, email: e.target.value })}
                  />
                </div>
                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingDebtor.phone||''}
                    onChange={e => setEditingDebtor({ ...editingDebtor, phone: e.target.value })}
                  />
                </div>
                {/* Email Notice */}
                <div className="flex items-center mt-6">
                  <input
                    type="checkbox"
                    checked={editingDebtor.debn_notice}
                    onChange={e => setEditingDebtor({ ...editingDebtor, debn_notice: e.target.checked })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Email Notice</label>
                </div>
              </div>
              {/* Primary & Mailing Address sections responsive */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">
                    Primary Address
                    <button
                      onClick={() => {
                    const other = caseData.debtors.find(d => d.id !== editingDebtor.id);
                        if (other) {
                          setEditingDebtor({
                            ...editingDebtor,
                            street: other.street || '',
                            street2: other.street2 || '',
                            city: other.city || '',
                            state: other.state || '',
                            zip_code: other.zip_code || '',
                            county: other.county || '',
                          });
                        }
                      }}
                      className="ml-2 text-sm text-indigo-600 hover:underline"
                    >
                      Copy from Other Debtor
                    </button>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Street</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.street||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, street: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Street 2</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.street2||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, street2: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.city||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.state||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, state: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Zip Code</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.zip_code||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, zip_code: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">County</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingDebtor.county||''}
                      onChange={e => setEditingDebtor({ ...editingDebtor, county: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-800">
                    Mailing Address
                    <button
                      onClick={() => {
                    const other = caseData.debtors.find(d => d.id !== editingDebtor.id);
                        if (other) {
                          setEditingDebtor({
                            ...editingDebtor,
                            mailing_street: other.mailing_street || '',
                            mailing_street2: other.mailing_street2 || '',
                            mailing_city: other.mailing_city || '',
                            mailing_state: other.mailing_state || '',
                            mailing_zip_code: other.mailing_zip_code || '',
                          });
                        }
                      }}
                      className="ml-2 text-sm text-indigo-600 hover:underline"
                    >
                      Copy Mailing Address
                    </button>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Street</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.mailing_street||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, mailing_street: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Street 2</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.mailing_street2||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, mailing_street2: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.mailing_city||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, mailing_city: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.mailing_state||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, mailing_state: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Zip Code</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border rounded px-2 py-1"
                        value={editingDebtor.mailing_zip_code||''}
                        onChange={e => setEditingDebtor({ ...editingDebtor, mailing_zip_code: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              {editingDebtor.id && !editingDebtor.is_primary && (
                <button
                  onClick={async () => {
                    if (!window.confirm("Are you sure you want to delete this debtor?")) return;
                    try {
                      const resp = await fetch(`${API}/case/${caseId}/debtors/${editingDebtor.id}/`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!resp.ok) throw new Error('Delete failed');
                      applyCaseData({
                        ...caseData,
                        debtors: caseData.debtors.filter(d => d.id !== editingDebtor.id),
                      });
                      await refreshCaseHome();
                      setShowEditDebtor(false);
                    } catch (err) {
                      console.error(err);
                      alert('Failed to delete debtor');
                    }
                  }}
                  className="px-2 py-1 bg-red-600 text-white rounded text-sm"
                >
                  Remove
                </button>
              )}
              <button
                onClick={async () => {
                  try {
                    let saved;
                    if (editingDebtor.id) {
                      const resp = await fetch(`${API}/case/${caseId}/debtors/${editingDebtor.id}/`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(editingDebtor),
                      });
                      if (!resp.ok) throw new Error('Update failed');
                      saved = await resp.json();
                    } else {
                      const resp = await fetch(`${API}/case/${caseId}/debtors/`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(editingDebtor),
                      });
                      if (!resp.ok) throw new Error('Create failed');
                      saved = await resp.json();
                    }
                    const updatedList = editingDebtor.id
                      ? caseData.debtors.map(d => d.id === saved.id ? saved : d)
                      : [...caseData.debtors, saved];
                    applyCaseData({ ...caseData, debtors: updatedList });
                    await refreshCaseHome();
                    setShowEditDebtor(false);
                  } catch (err) {
                    console.error(err);
                    alert('Failed to save debtor');
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded"
                ref={saveDebtorRef}
              >
                Save
              </button>
              <button
                onClick={() => setShowEditDebtor(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Property Edit Modal */}
      {showPropertyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingProperty.id ? 'Edit Property' : 'Add Property'}
            </h2>
            <div className="space-y-4">
              {/* Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {['street1','street2'].map(f => (
                  <div key={f}>
                    <label className="block text-sm font-medium text-gray-700">{f.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingProperty[f]||''}
                      onChange={e => setEditingProperty({ ...editingProperty, [f]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {['city','state','zip_code'].map(f => (
                  <div key={f}>
                    <label className="block text-sm font-medium text-gray-700">{f.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border rounded px-2 py-1"
                      value={editingProperty[f]||''}
                      onChange={e => setEditingProperty({ ...editingProperty, [f]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              {/* Other fields */}
              {['county','monthly_payment','second_monthly_payment','value_total','value_ownership_portion','property_type','ownership','ownership_type','other_info','exemption_code','exemption_amount'].map(f => (
                <div key={f}>
                  <label className="block text-sm font-medium text-gray-700">{f.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</label>
                  <input
                    type={f.includes('payment')||f.includes('value')||f.includes('amount') ? 'number' : 'text'}
                    step="0.01"
                    className="mt-1 block w-full border rounded px-2 py-1"
                    value={editingProperty[f]||''}
                    onChange={e => setEditingProperty({ ...editingProperty, [f]: e.target.value })}
                  />
                </div>
              ))}
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingProperty.principal_residence}
                    onChange={e => setEditingProperty({ ...editingProperty, principal_residence: e.target.checked })}
                    className="mr-2"
                  />
                  Principal Residence
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingProperty.community_property}
                    onChange={e => setEditingProperty({ ...editingProperty, community_property: e.target.checked })}
                    className="mr-2"
                  />
                  Community Property
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              {editingProperty.id && (
                <button
                  onClick={async () => {
                    if (!window.confirm("Delete this property?")) return;
                    await fetch(`${API}/case/${caseId}/real-properties/${editingProperty.id}/delete/`, {
                      method: 'DELETE',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    applyCaseData({
                      ...caseData,
                      real_properties: caseData.real_properties.filter(p => p.id !== editingProperty.id),
                    });
                    setShowPropertyModal(false);
                  }}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                >
                  Remove
                </button>
              )}
              <button
                onClick={async () => {
                  const method = editingProperty.id ? 'PUT' : 'POST';
                  const url = editingProperty.id
                    ? `${API}/case/${caseId}/real-properties/${editingProperty.id}/`
                    : `${API}/case/${caseId}/real-properties/`;
                  const res = await fetch(url, {
                    method,
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(editingProperty),
                  });
                  const saved = await res.json();
                  applyCaseData({
                    ...caseData,
                    real_properties: editingProperty.id
                      ? caseData.real_properties.map(p => p.id === saved.id ? saved : p)
                      : [...caseData.real_properties, saved],
                  });
                  setShowPropertyModal(false);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded"
                ref={savePropertyRef}
              >
                Save
              </button>
              <button
                onClick={() => setShowPropertyModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
